// react
import {
	ChangeEvent,
	Dispatch,
	FormEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import {
	CashInMethod,
	CashInMethodPayload,
	ErrorCause,
	Feedback,
	TransactionType,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// ui
import { Loading } from '../../ui/Loading/Loading';

// unions
import { transactionTypesIlap } from '../../../@types/unions';

// utils
import { translateTransactionType } from '../../../utils/translations';

// props
export interface FormEditCashInMethodProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditCashInMethod = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormEditCashInMethodProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [cashInMethod, setCashInMethod] = useState<CashInMethod | null>(null);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [searchParams] = useSearchParams();
	const [values, setValues] = useState({
		name: '',
		transactionTypes: [] as TransactionType[],
	});

	// params
	const paramCashInMethod = searchParams.get('dialogCashInMethod');

	// context
	const { setBanner } = useContext(ContextBanner);

	// vars
	const idForm = 'form-edit-cash-in-method';

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// transaction types options
	const optionsTransactionTypes = transactionTypesIlap.map((type) => ({
		label: translateTransactionType(type, t),
		value: type,
	}));

	// fetch cash-in method
	const { isLoading: isLoadingCashInMethod } = useFetch({
		isEnabled: Boolean(paramCashInMethod),
		url: `${process.env.REACT_APP_API_URL}/cash-in-methods/${paramCashInMethod}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCashInMethod(res.data[0]);
			}
		},
	});

	// update values when cash-in method loads
	useEffect(() => {
		if (cashInMethod) {
			setValues({
				name: cashInMethod.name || '',
				transactionTypes: cashInMethod.transactionTypes || [],
			});
		}
	}, [cashInMethod]);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear feedback
			setFeedback(null);

			// set loading
			setIsLoading(true);

			if (!paramCashInMethod) {
				throw new Error('An ID is required', {
					cause: {
						id: 'ID_NON_EXISTENT',
					},
				});
			}

			const payload: Partial<CashInMethodPayload> = {
				name: values.name,
				transactionTypes: values.transactionTypes,
			};

			const fetchEditCashInMethod = await fetch(
				`${process.env.REACT_APP_API_URL}/cash-in-methods/${paramCashInMethod}`,
				{
					method: 'PATCH',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(payload),
				}
			);
			const resEditCashInMethod = await fetchEditCashInMethod.json();

			if (resEditCashInMethod.error) {
				throw new Error(resEditCashInMethod.error.message, {
					cause: {
						id: resEditCashInMethod.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.cashInMethodUpdated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'CASH_IN_METHOD_UNAUTHORIZED') {
					feedbackNew.alert.children = t('error.unauthorized');
				} else if (cause.id === 'CASH_IN_METHOD_DEFAULT_CANNOT_UPDATE') {
					feedbackNew.alert.children = t('error.cashInMethodDefaultCannotUpdate');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			setIsLoading(false);
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	if (isLoadingCashInMethod) {
		return <Loading />;
	}

	return (
		<form id={idForm} name="formEditCashInMethod" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12}>
					<Select
						id={`${idForm}-transaction-types`}
						label={t('transactionTypes')}
						multiple={true}
						name="transactionTypes"
						options={optionsTransactionTypes}
						value={values.transactionTypes}
						onChange={handleChangeValue}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
